import { useCallback, useEffect, useState, useRef } from 'react'
import { STATUS, UNAUTHORIZED_TYPES } from 'utils/constants'
import useTranslation from 'hooks/useTranslation'
import Submit from 'components/inputs/Submit'
import Popup from 'components/popups/Popup'
import { selectForm } from '../formSlice'
import { useSelector } from 'react-redux'
import { POST } from 'rest/request'
import { verify } from 'rest/urls'

const INIT_CODE = { 0: '', 1: '', 2: '', 3: '', 4: '', 5: '' }
const CODE_LENGTH = 6

const VerificationPopup = ({ loginUser, close }) => {
  const { phone = '' } = useSelector(selectForm)
  const [status, setStatus] = useState(STATUS.idle)
  const [warning, setWarning] = useState(false)
  const [code, setCode] = useState(INIT_CODE)

  const refs = []
  const autoCodeLoaderRef = useRef(null)
  const codeToString = Object.entries(code).reduce((r, [, v]) => r += v, '')

  const { t } = useTranslation('auth')
  const validateCode = useCallback(async () => {
    setStatus(STATUS.pending)
    if (autoCodeLoaderRef.current) {
      autoCodeLoaderRef.current?.abort()
      autoCodeLoaderRef.current = null
    }

    try {
      const body = { phone, token: codeToString }
      const response = await POST(verify(UNAUTHORIZED_TYPES.phone), { body })

      if (response) {
        setStatus(STATUS.succeed)
        loginUser()
      }

    } catch (err) {
      setStatus(STATUS.failed)
    }
  }, [codeToString, loginUser, phone])

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => refs[0] && refs[0]?.focus(), [])
  useEffect(() => {
    if ('OTPCredential' in window) {
      const loadCodeFromSMS = () => {
        console.log('DOMContentLoaded event')
        const ac = new AbortController()

        autoCodeLoaderRef.current = ac
        navigator.credentials.get({
          otp: { transport: ['sms'] },
          signal: ac.signal
        }).then(otp => {
          alert(otp.code)
          if (otp.code) {
            const codeList = otp.code.split('')
            setCode({ ...codeList })
            validateCode()
          }
        }).catch(err => {
          console.warn(err)
        })
      }

      window.addEventListener('DOMContentLoaded', loadCodeFromSMS)
      return () => {
        window.removeEventListener('DOMContentLoaded', loadCodeFromSMS)
      }
    }
  }, [validateCode])

  const getSubmitText = len => len === CODE_LENGTH
    ? t('authorize')
    : `${CODE_LENGTH - len} ${t(len === CODE_LENGTH - 1 ? 'characterIsMissing' : 'charactersAreMissing')}`

  const moveFocus = (ref, forward = true) => {
    const step = forward ? 1 : -1
    const index = refs.findIndex(r => Object.is(r, ref))
    const key = index > -1 ? index + step : undefined

    if (key !== undefined && refs[key]) {
      const len = code[key].length
      refs[key].setSelectionRange(0, len)
      refs[key].focus()
    }
  }

  return (
    <Popup close={close}>
      <div className='popup__verification'>
        <i className='icon icon--logo s24' />
        <h2>{t('authorization')}</h2>

        <div className='verification__text'>
          <div>{t('rewriteTheVerificationCode')}</div>
          <div>{t('sentToNumber')}:</div>
        </div>

        <div className='verification__phone'>
          <h2>{phone.replace(/^([\d]{3})([\d]{3})([\d]{3})$/, '$1 $2 $3')}</h2>
        </div>

        <div className={`verification__code group${warning ? ' --warning' : ''}`}>
          {Object.entries(code).map(([k, v]) => <input
            required
            key={k}
            value={v}
            type='tel'
            ref={r => refs.push(r)}
            disabled={status === STATUS.pending}
            onKeyUp={e => {
              if (e.key === 'Backspace' && !v) {
                moveFocus(e.target, false)
              } else if (e.key === 'Enter' && codeToString.length === CODE_LENGTH) {
                validateCode()
              }
            }}
            onChange={e => {
              const { value } = e.target
              const firstChar = value.charAt(0)

              if (value.length === CODE_LENGTH && value.match(/^[0-9]{6}$/)) {
                const list = value.split('')
                setCode({ ...list })
              }
              else if (!firstChar || firstChar.match(/^[0-9]{1}$/)) {
                setCode({
                  ...code,
                  [k]: firstChar
                })
                moveFocus(e.target, !!firstChar)
              }
            }}
          />)}
        </div>

        <div
          className='verification__button'
          onClick={() => {
            if (status === STATUS.pending) return
            setCode(INIT_CODE)
            setWarning(false)
          }}>
          {t('sendAgain')}
        </div>

        <Submit
          text={getSubmitText(codeToString.length)}
          block={codeToString.length !== CODE_LENGTH || status === STATUS.pending}
          action={validateCode}
        />
      </div>
    </Popup>
  )
}

export default VerificationPopup